import React, { useEffect, useState } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { trackEvent } from '../analytics/tracker';
import { MotionConfig, motion, type Variants } from 'framer-motion';
import {
  ArrowRight,
  BarChart3,
  Check,
  CheckCircle2,
  ChevronDown,
  ClipboardList,
  Download,
  Layers,
  Package,
  Play,
  QrCode,
  Quote,
  Receipt,
  Store,
  Users,
  WifiOff,
  X,
  Zap,
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useSectionNavigation } from '../hooks/useSectionNavigation';

const APP_LINK = 'https://play.google.com/store/apps/details?id=com.swift.pos&hl=en';
const EASE: [number, number, number, number] = [0.22, 1, 0.36, 1];

/* PRICING PLACEHOLDER — confirm the Pro monthly price before running ads.
   Change it in LanguageContext (pos_price_pro) — this file needs no edit. */

/* Stagger container / item for section reveals */
const staggerParent: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.07, delayChildren: 0.05 } },
};
const staggerChild: Variants = {
  hidden: { opacity: 0, y: 26 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE } },
};

const Reveal: React.FC<{ children: React.ReactNode; className?: string; delay?: number }> = ({
  children,
  className,
  delay = 0,
}) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-64px' }}
    transition={{ duration: 0.65, delay, ease: EASE }}
  >
    {children}
  </motion.div>
);

/* Illustrative checkout mockup (interface illustration, not a screenshot). */
const PhoneMockup: React.FC = () => (
  <motion.div
    aria-hidden="true"
    className="relative mx-auto w-[272px] sm:w-[300px]"
    animate={{ y: [0, -10, 0] }}
    transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
  >
    {/* Floating chips */}
    <motion.div
      className="absolute -left-16 sm:-left-20 top-16 z-10 flex items-center px-3 py-2 rounded-xl text-[10px] font-black whitespace-nowrap"
      style={{ backgroundColor: 'var(--surface-elevated)', border: '1px solid var(--border)', color: 'var(--text-soft)', boxShadow: '0 12px 32px -12px rgba(0,0,0,0.4)', gap: 'var(--icon-label-gap-sm)' }}
      animate={{ y: [0, -8, 0] }}
      transition={{ duration: 5.5, repeat: Infinity, ease: 'easeInOut', delay: 0.6 }}
    >
      <WifiOff className="w-3.5 h-3.5" style={{ color: 'var(--success)' }} />
      Offline-ready
    </motion.div>
    <motion.div
      className="absolute -right-12 sm:-right-16 bottom-24 z-10 flex items-center px-3 py-2 rounded-xl text-[10px] font-black whitespace-nowrap"
      style={{ backgroundColor: 'var(--surface-elevated)', border: '1px solid var(--border)', color: 'var(--text-soft)', boxShadow: '0 12px 32px -12px rgba(0,0,0,0.4)', gap: 'var(--icon-label-gap-sm)' }}
      animate={{ y: [0, 8, 0] }}
      transition={{ duration: 6.5, repeat: Infinity, ease: 'easeInOut', delay: 1.2 }}
    >
      <QrCode className="w-3.5 h-3.5" style={{ color: 'var(--primary)' }} />
      QR Menu
    </motion.div>

    <div
      className="rounded-[2.2rem] p-2.5"
      style={{ backgroundColor: 'var(--surface-elevated)', border: '1px solid var(--border)', boxShadow: '0 40px 90px -32px rgba(var(--primary-rgb), 0.5)' }}
    >
      <div className="rounded-[1.8rem] overflow-hidden" style={{ backgroundColor: 'var(--bg)' }}>
        <div className="flex items-center justify-between px-5 pt-4 pb-2">
          <span className="text-[11px] font-black tracking-tight" style={{ color: 'var(--text)' }}>SwiftPOS</span>
          <span className="relative flex items-center text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(var(--success-rgb), 0.12)', color: 'var(--success)', gap: 'var(--icon-label-gap-sm)' }}>
            <span className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ backgroundColor: 'var(--success)' }} />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5" style={{ backgroundColor: 'var(--success)' }} />
            </span>
            Open
          </span>
        </div>
        <div className="grid grid-cols-3 gap-1.5 px-4 py-2">
          {['Espresso', 'Latte', 'Croissant', 'Sandwich', 'Cake', 'Tea'].map((p, i) => (
            <motion.div
              key={p}
              className="rounded-lg py-2 text-center"
              style={{ backgroundColor: i === 0 ? 'rgba(var(--primary-rgb), 0.14)' : 'rgba(var(--bg-rgb), 0.05)', border: `1px solid ${i === 0 ? 'rgba(var(--primary-rgb), 0.4)' : 'var(--border)'}` }}
              initial={{ opacity: 0, scale: 0.85 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.9 + i * 0.08, duration: 0.4, ease: EASE }}
            >
              <p className="text-[8px] font-bold truncate px-1" style={{ color: 'var(--text-soft)' }}>{p}</p>
            </motion.div>
          ))}
        </div>
        <motion.div
          className="mx-4 rounded-xl p-3"
          style={{ backgroundColor: 'rgba(var(--bg-rgb), 0.04)', border: '1px solid var(--border)' }}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4, duration: 0.5, ease: EASE }}
        >
          {[
            ['Espresso', '$2.50'],
            ['Croissant', '$3.00'],
            ['Latte', '$4.25'],
          ].map(([name, price]) => (
            <div key={name} className="flex justify-between py-1">
              <span className="text-[10px] font-medium" style={{ color: 'var(--text-soft)' }}>{name}</span>
              <span className="text-[10px] font-black tabular-nums" style={{ color: 'var(--text)' }}>{price}</span>
            </div>
          ))}
          <div className="flex justify-between pt-2 mt-1" style={{ borderTop: '1px dashed var(--border)' }}>
            <span className="text-[10px] font-black" style={{ color: 'var(--text)' }}>Total</span>
            <span className="text-[12px] font-black tabular-nums" style={{ color: 'var(--primary)' }}>$9.75</span>
          </div>
        </motion.div>
        <div className="p-4">
          <motion.div
            className="rounded-xl py-2.5 text-center text-[11px] font-black text-white"
            style={{ backgroundColor: 'var(--primary)' }}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.55, duration: 0.5, ease: EASE }}
            whileHover={{ scale: 1.02 }}
          >
            Charge $9.75
          </motion.div>
        </div>
      </div>
    </div>
  </motion.div>
);

/* Illustrative dashboard mockup with animated bars (illustration, not real data). */
const DashboardMockup: React.FC = () => {
  const bars = [38, 62, 45, 78, 55, 90, 68];
  const products: Array<[string, number]> = [
    ['Espresso', 92],
    ['Croissant', 74],
    ['Latte', 58],
  ];
  return (
    <motion.div
      aria-hidden="true"
      className="rounded-2xl overflow-hidden"
      style={{ backgroundColor: 'var(--surface-elevated)', border: '1px solid var(--border)', boxShadow: '0 40px 90px -44px rgba(0, 0, 0, 0.55)' }}
      initial={{ opacity: 0, y: 36, scale: 0.98 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.7, ease: EASE }}
      whileHover={{ scale: 1.008 }}
    >
      <div className="flex items-center px-4 py-3" style={{ borderBottom: '1px solid var(--border)', gap: 'var(--icon-label-gap-sm)' }}>
        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: 'rgba(var(--error-rgb), 0.5)' }} />
        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: 'rgba(var(--warning-rgb), 0.5)' }} />
        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: 'rgba(var(--success-rgb), 0.5)' }} />
        <span className="ml-2 text-[10px] font-bold" style={{ color: 'var(--text-muted)' }}>SwiftPOS Dashboard</span>
      </div>
      <div className="grid grid-cols-[44px_1fr] sm:grid-cols-[56px_1fr]">
        <div className="flex flex-col items-center gap-3 py-4" style={{ borderRight: '1px solid var(--border)' }}>
          {[Zap, Package, ClipboardList, Users, BarChart3].map((Icon, i) => (
            <span key={i} className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: i === 0 ? 'var(--primary)' : 'rgba(var(--bg-rgb), 0.05)', color: i === 0 ? 'white' : 'var(--text-muted)' }}>
              <Icon className="w-3.5 h-3.5" />
            </span>
          ))}
        </div>
        <div className="p-4 sm:p-5">
          <div className="flex items-end gap-2 h-24 sm:h-28 mb-4">
            {bars.map((h, i) => (
              <motion.div
                key={i}
                className="flex-1 rounded-t-md origin-bottom"
                style={{ backgroundColor: i === 5 ? 'var(--primary)' : 'rgba(var(--primary-rgb), 0.25)' }}
                initial={{ height: '6%' }}
                whileInView={{ height: `${h}%` }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.8, delay: i * 0.07, ease: EASE }}
              />
            ))}
          </div>
          <div className="space-y-2.5">
            {products.map(([name, w], i) => (
              <div key={name}>
                <p className="text-[10px] font-bold mb-1" style={{ color: 'var(--text-soft)' }}>{name}</p>
                <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'rgba(var(--bg-rgb), 0.06)' }}>
                  <motion.div
                    className="h-full rounded-full origin-left"
                    style={{ backgroundColor: 'var(--accent)' }}
                    initial={{ width: '4%' }}
                    whileInView={{ width: `${w}%` }}
                    viewport={{ once: true, margin: '-60px' }}
                    transition={{ duration: 0.9, delay: 0.3 + i * 0.12, ease: EASE }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const FEATURES: Array<{ icon: React.ElementType; titleKey: string; descKey: string }> = [
  { icon: Zap, titleKey: 'pos_feat_sales', descKey: 'pos_feat_sales_desc' },
  { icon: Package, titleKey: 'pos_feat_inventory', descKey: 'pos_feat_inventory_desc' },
  { icon: ClipboardList, titleKey: 'pos_feat_orders', descKey: 'pos_feat_orders_desc' },
  { icon: Users, titleKey: 'pos_feat_customers', descKey: 'pos_feat_customers_desc' },
  { icon: BarChart3, titleKey: 'pos_feat_reports', descKey: 'pos_feat_reports_desc' },
  { icon: QrCode, titleKey: 'pos_feat_qrmenu', descKey: 'pos_feat_qrmenu_desc' },
  { icon: Receipt, titleKey: 'pos_feat_receipts', descKey: 'pos_feat_receipts_desc' },
  { icon: WifiOff, titleKey: 'pos_feat_offline', descKey: 'pos_feat_offline_desc' },
];

const WHY: Array<{ icon: React.ElementType; titleKey: string; descKey: string }> = [
  { icon: CheckCircle2, titleKey: 'pos_why_simple_t', descKey: 'pos_why_simple_d' },
  { icon: Zap, titleKey: 'pos_why_fast_t', descKey: 'pos_why_fast_d' },
  { icon: Layers, titleKey: 'pos_why_powerful_t', descKey: 'pos_why_powerful_d' },
  { icon: Store, titleKey: 'pos_why_flex_t', descKey: 'pos_why_flex_d' },
];

const STEPS: Array<{ icon: React.ElementType; titleKey: string; descKey: string }> = [
  { icon: Download, titleKey: 'pos_step1_t', descKey: 'pos_step1_d' },
  { icon: Package, titleKey: 'pos_step2_t', descKey: 'pos_step2_d' },
  { icon: Zap, titleKey: 'pos_step3_t', descKey: 'pos_step3_d' },
];

const TESTIS: Array<{ q: string; n: string }> = [
  { q: 'pos_t1_q', n: 'pos_t1_n' },
  { q: 'pos_t2_q', n: 'pos_t2_n' },
  { q: 'pos_t3_q', n: 'pos_t3_n' },
];

const FAQS: Array<{ q: string; a: string }> = [
  { q: 'pos_faq1_q', a: 'pos_faq1_a' },
  { q: 'pos_faq2_q', a: 'pos_faq2_a' },
  { q: 'pos_faq3_q', a: 'pos_faq3_a' },
  { q: 'pos_faq4_q', a: 'pos_faq4_a' },
  { q: 'pos_faq5_q', a: 'pos_faq5_a' },
  { q: 'pos_faq6_q', a: 'pos_faq6_a' },
];

const SwiftPOS: React.FC = () => {
  const { t, language } = useLanguage();
  const { scrollToSection } = useSectionNavigation();
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [showSticky, setShowSticky] = useState(false);
  const [stickyClosed, setStickyClosed] = useState(false);

  /* Per-page SEO for ad-landing relevance (+ SoftwareApplication schema). */
  useEffect(() => {
    document.title = t('pos_seo_title');
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', t('pos_seo_desc'));
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'swiftpos-schema';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'MobileApplication',
      name: 'SwiftPOS',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Android',
      inLanguage: ['en', 'ar'],
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    });
    document.head.appendChild(script);
    return () => {
      document.getElementById('swiftpos-schema')?.remove();
    };
  }, [language, t]);

  /* Sticky mobile install bar appears after the hero is scrolled past. */
  useEffect(() => {
    const onScroll = () => setShowSticky(window.scrollY > 550);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const stats = [
    { value: '8', labelKey: 'pos_stat_modules_l' },
    { value: '100%', labelKey: 'pos_stat_offline_l' },
    { value: '2', labelKey: 'pos_stat_lang_l' },
    { value: '1', labelKey: 'pos_stat_all_l' },
  ];

  return (
    <MotionConfig reducedMotion="user">
      <div className="sw-app">
        <Header />

        <main id="main-content" role="main" tabIndex={-1}>
          {/* ============ 1. HERO ============ */}
          <section className="relative overflow-hidden" aria-labelledby="swiftpos-title">
            <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
              <motion.div
                className="absolute -top-32 left-1/2 w-[700px] h-[420px] rounded-full blur-[130px]"
                style={{ backgroundColor: 'rgba(var(--primary-rgb), 0.12)', x: '-50%' }}
                animate={{ opacity: [0.7, 1, 0.7], scale: [1, 1.06, 1] }}
                transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
              />
              <div
                className="absolute inset-0 opacity-[0.025]"
                style={{ backgroundImage: 'radial-gradient(var(--text) 1px, transparent 1px)', backgroundSize: '30px 30px' }}
              />
            </div>
            <div className="sw-container relative z-10 pt-24 md:pt-32 pb-14 md:pb-20">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-8 items-center">
                <div className="text-center lg:text-left">
                  <motion.div
                    initial="hidden"
                    animate="show"
                    variants={staggerParent}
                    className="flex flex-col items-center lg:items-start"
                  >
                    <motion.p
                      variants={staggerChild}
                      className="inline-flex items-center px-4 py-1.5 rounded-full mb-5 text-[11px] font-bold uppercase tracking-widest"
                      style={{ backgroundColor: 'rgba(var(--primary-rgb), 0.08)', border: '1px solid rgba(var(--primary-rgb), 0.2)', color: 'var(--primary)', gap: 'var(--icon-label-gap-sm)' }}
                    >
                      {t('pos_hero_badge')}
                    </motion.p>
                    <motion.h1
                      variants={staggerChild}
                      id="swiftpos-title"
                      dir="auto"
                      className="text-4xl sm:text-5xl xl:text-6xl font-black leading-[1.08] tracking-tight mb-4"
                      style={{ color: 'var(--text)' }}
                    >
                      {t('pos_simple_title')}
                      <span className="block gradient-text py-1">{t('pos_simple_sub')}</span>
                    </motion.h1>
                    <motion.p
                      variants={staggerChild}
                      dir="auto"
                      className="text-base md:text-lg max-w-xl leading-relaxed mb-7"
                      style={{ color: 'var(--text-soft)' }}
                    >
                      {t('pos_simple_desc')}
                    </motion.p>
                    <motion.div
                      variants={staggerChild}
                      className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start w-full sm:w-auto"
                      role="group"
                      aria-label="Get SwiftPOS actions"
                    >
                      <motion.a
                        href={APP_LINK}
                        target="_blank"
                        rel="noreferrer"
                        className="sw-btn sw-btn-primary btn-lg"
                        whileHover={{ scale: 1.03, y: -2 }}
                        whileTap={{ scale: 0.97 }}
                        onClick={() => trackEvent('swiftpos_get', undefined, { label: 'hero_play', page: 'swiftpos' })}
                      >
                        <Play className="w-4 h-4 fill-current" aria-hidden="true" />
                        {t('pos_hero_play')}
                      </motion.a>
                      <motion.a
                        href="#features"
                        className="sw-btn sw-btn-secondary btn-lg"
                        whileHover={{ scale: 1.03, y: -2 }}
                        whileTap={{ scale: 0.97 }}
                        onClick={(e) => {
                          trackEvent('cta_click', undefined, { label: 'explore_features', location: 'swiftpos_hero' });
                          scrollToSection(e, 'features');
                        }}
                      >
                        {t('pos_btn_features')}
                        <ArrowRight className="w-4 h-4 rtl:rotate-180" aria-hidden="true" />
                      </motion.a>
                    </motion.div>
                    <motion.p
                      variants={staggerChild}
                      dir="auto"
                      className="text-xs font-bold mt-3"
                      style={{ color: 'var(--text-muted)' }}
                    >
                      {t('pos_hero_free_note')}
                    </motion.p>
                    <motion.div
                      variants={staggerChild}
                      className="flex flex-wrap items-center justify-center lg:justify-start gap-x-5 gap-y-2 mt-5"
                    >
                      {[
                        { icon: CheckCircle2, key: 'pos_trust_free' },
                        { icon: WifiOff, key: 'pos_trust_offline' },
                      ].map((item) => (
                        <span key={item.key} className="inline-flex items-center text-[13px] font-bold" style={{ color: 'var(--text-muted)', gap: 'var(--icon-label-gap-sm)' }}>
                          <item.icon className="w-4 h-4" style={{ color: 'var(--success)' }} aria-hidden="true" />
                          {t(item.key)}
                        </span>
                      ))}
                    </motion.div>
                  </motion.div>
                </div>
                <motion.div
                  initial={{ opacity: 0, y: 40, scale: 0.97 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ duration: 0.8, delay: 0.35, ease: EASE }}
                >
                  <PhoneMockup />
                </motion.div>
              </div>
            </div>
          </section>

          {/* ============ 2. STATS STRIP ============ */}
          <section aria-label="SwiftPOS highlights" style={{ backgroundColor: 'var(--surface)', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}>
            <div className="sw-container py-8">
              <dl className="grid grid-cols-2 lg:grid-cols-4 gap-6 text-center">
                {stats.map((s) => (
                  <div key={s.labelKey}>
                    <dt className="order-2 text-xs font-bold uppercase tracking-widest mt-1" dir="auto" style={{ color: 'var(--text-muted)' }}>{t(s.labelKey)}</dt>
                    <dd className="order-1 text-3xl md:text-4xl font-black tabular-nums" style={{ color: 'var(--primary)' }}>{s.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </section>

          {/* ============ 3. MAIN FEATURES ============ */}
          <section id="features" className="sw-section" role="region" aria-labelledby="swiftpos-features-title" style={{ scrollMarginTop: 96 }}>
            <div className="sw-container">
              <Reveal className="text-center max-w-2xl mx-auto mb-10">
                <span className="sw-eyebrow">{t('pos_features_eyebrow')}</span>
                <h2 id="swiftpos-features-title" dir="auto" className="text-3xl md:text-4xl font-black tracking-tight" style={{ color: 'var(--text)' }}>
                  {t('pos_simple_feat_title')}
                </h2>
              </Reveal>
              <motion.div
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: '-64px' }}
                variants={staggerParent}
              >
                {FEATURES.map((f) => (
                  <motion.article key={f.titleKey} variants={staggerChild} className="h-full">
                    <motion.div
                      className="sw-card group p-6 h-full"
                      whileHover={{ y: -6 }}
                      transition={{ duration: 0.25, ease: EASE }}
                    >
                      <motion.span
                        className="w-11 h-11 rounded-xl flex items-center justify-center mb-4"
                        style={{ backgroundColor: 'rgba(var(--primary-rgb), 0.1)', color: 'var(--primary)' }}
                        whileHover={{ scale: 1.1, rotate: -6 }}
                        transition={{ duration: 0.25 }}
                      >
                        <f.icon className="w-5 h-5" aria-hidden="true" />
                      </motion.span>
                      <h3 dir="auto" className="font-bold text-[1.05rem] mb-1.5" style={{ color: 'var(--text)' }}>{t(f.titleKey)}</h3>
                      <p dir="auto" className="text-sm leading-relaxed" style={{ color: 'var(--text-muted)' }}>{t(f.descKey)}</p>
                    </motion.div>
                  </motion.article>
                ))}
              </motion.div>
            </div>
          </section>

          {/* ============ 4. HOW IT WORKS ============ */}
          <section className="sw-section" role="region" aria-labelledby="swiftpos-steps-title" style={{ backgroundColor: 'var(--surface)', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)', paddingTop: 0 }}>
            <div className="sw-container" style={{ paddingTop: 'var(--section-y)' }}>
              <Reveal className="text-center max-w-2xl mx-auto mb-10">
                <span className="sw-eyebrow">{t('pos_steps_eyebrow')}</span>
                <h2 id="swiftpos-steps-title" dir="auto" className="text-3xl md:text-4xl font-black tracking-tight" style={{ color: 'var(--text)' }}>
                  {t('pos_steps_title')}
                </h2>
              </Reveal>
              <motion.ol
                className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-5xl mx-auto list-none"
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: '-64px' }}
                variants={staggerParent}
              >
                {STEPS.map((s, i) => (
                  <motion.li key={s.titleKey} variants={staggerChild} className="sw-card relative p-6 pt-8 text-center h-full">
                    <span
                      aria-hidden="true"
                      className="absolute top-4 start-5 text-4xl font-black tabular-nums"
                      style={{ color: 'rgba(var(--primary-rgb), 0.18)' }}
                    >
                      {i + 1}
                    </span>
                    <span
                      className="w-12 h-12 rounded-2xl inline-flex items-center justify-center mb-4"
                      style={{ backgroundColor: 'var(--primary)', color: 'white' }}
                    >
                      <s.icon className="w-5 h-5" aria-hidden="true" />
                    </span>
                    <h3 dir="auto" className="font-black text-lg mb-1.5" style={{ color: 'var(--text)' }}>{t(s.titleKey)}</h3>
                    <p dir="auto" className="text-sm leading-relaxed" style={{ color: 'var(--text-muted)' }}>{t(s.descKey)}</p>
                  </motion.li>
                ))}
              </motion.ol>
            </div>
          </section>

          {/* ============ 5. WHY SWIFTPOS ============ */}
          <section className="sw-section" role="region" aria-labelledby="swiftpos-why-title">
            <div className="sw-container">
              <Reveal className="text-center mb-9">
                <h2 id="swiftpos-why-title" dir="auto" className="text-2xl md:text-3xl font-black tracking-tight" style={{ color: 'var(--text)' }}>
                  {t('pos_why_title')}
                </h2>
              </Reveal>
              <motion.div
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-7 md:gap-8 max-w-5xl mx-auto"
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: '-64px' }}
                variants={staggerParent}
              >
                {WHY.map((w) => (
                  <motion.div key={w.titleKey} variants={staggerChild} className="text-center">
                    <motion.span
                      className="inline-flex w-12 h-12 rounded-2xl items-center justify-center mb-3"
                      style={{ backgroundColor: 'rgba(var(--accent-rgb), 0.1)', color: 'var(--accent)' }}
                      whileHover={{ scale: 1.12, rotate: 6 }}
                      transition={{ duration: 0.25 }}
                    >
                      <w.icon className="w-5 h-5" aria-hidden="true" />
                    </motion.span>
                    <h3 dir="auto" className="font-black mb-1" style={{ color: 'var(--text)' }}>{t(w.titleKey)}</h3>
                    <p dir="auto" className="text-sm leading-relaxed" style={{ color: 'var(--text-muted)' }}>{t(w.descKey)}</p>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </section>

          {/* ============ 6. PRODUCT PREVIEW ============ */}
          <section className="sw-section" role="region" aria-labelledby="swiftpos-preview-title" style={{ paddingTop: 0 }}>
            <div className="sw-container">
              <Reveal className="text-center max-w-2xl mx-auto mb-8">
                <h2 id="swiftpos-preview-title" dir="auto" className="text-3xl md:text-4xl font-black tracking-tight mb-3" style={{ color: 'var(--text)' }}>
                  {t('pos_prev_title')}
                </h2>
                <p dir="auto" className="leading-relaxed" style={{ color: 'var(--text-muted)' }}>
                  {t('pos_prev_desc')}
                </p>
              </Reveal>
              <div className="max-w-4xl mx-auto">
                <DashboardMockup />
              </div>
            </div>
          </section>

          {/* ============ 7. PRICING ============ */}
          <section className="sw-section" role="region" aria-labelledby="swiftpos-pricing-title" style={{ backgroundColor: 'var(--surface)', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}>
            <div className="sw-container">
              <Reveal className="text-center max-w-2xl mx-auto mb-10">
                <span className="sw-eyebrow">{t('pos_price_eyebrow')}</span>
                <h2 id="swiftpos-pricing-title" dir="auto" className="text-3xl md:text-4xl font-black tracking-tight mb-3" style={{ color: 'var(--text)' }}>
                  {t('pos_price_title')}
                </h2>
                <p dir="auto" className="leading-relaxed" style={{ color: 'var(--text-muted)' }}>
                  {t('pos_price_desc')}
                </p>
              </Reveal>
              <motion.div
                className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto items-stretch"
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: '-64px' }}
                variants={staggerParent}
              >
                <motion.div variants={staggerChild} className="sw-card p-7 flex flex-col h-full">
                  <h3 dir="auto" className="font-black text-xl" style={{ color: 'var(--text)' }}>{t('pos_plan_free')}</h3>
                  <p dir="auto" className="text-sm mt-1 mb-5 leading-relaxed" style={{ color: 'var(--text-muted)' }}>{t('pos_plan_free_d')}</p>
                  <p className="mb-5">
                    <span className="text-4xl font-black tabular-nums" style={{ color: 'var(--text)' }}>{t('pos_price_free')}</span>
                  </p>
                  <ul className="space-y-2.5 mb-7 text-sm font-bold" style={{ color: 'var(--text-soft)' }}>
                    {[t('pos_feat_sales'), t('pos_feat_inventory'), t('pos_feat_orders'), t('pos_feat_qrmenu'), t('pos_plan_limit')].map((f) => (
                      <li key={f} dir="auto" className="flex items-start" style={{ gap: 'var(--icon-label-gap-sm)' }}>
                        <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: 'var(--success)' }} aria-hidden="true" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <a
                    href={APP_LINK}
                    target="_blank"
                    rel="noreferrer"
                    className="sw-btn sw-btn-secondary btn-lg w-full mt-auto"
                    onClick={() => trackEvent('swiftpos_get', undefined, { label: 'pricing_free', page: 'swiftpos' })}
                  >
                    <Download className="w-4 h-4" aria-hidden="true" />
                    {t('pos_plan_cta_free')}
                  </a>
                </motion.div>
                <motion.div variants={staggerChild} className="sw-card relative p-7 flex flex-col h-full" style={{ border: '2px solid var(--primary)', boxShadow: '0 24px 60px -24px rgba(var(--primary-rgb), 0.5)' }}>
                  <span
                    className="absolute -top-3.5 start-6 text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full text-white"
                    style={{ backgroundColor: 'var(--primary)' }}
                  >
                    {t('pos_popular')}
                  </span>
                  <h3 dir="auto" className="font-black text-xl" style={{ color: 'var(--text)' }}>{t('pos_plan_pro')}</h3>
                  <p dir="auto" className="text-sm mt-1 mb-5 leading-relaxed" style={{ color: 'var(--text-muted)' }}>{t('pos_plan_pro_d')}</p>
                  <p className="mb-1">
                    <span className="text-4xl font-black tabular-nums" style={{ color: 'var(--primary)' }}>{t('pos_price_pro')}</span>
                    <span className="text-sm font-bold" style={{ color: 'var(--text-muted)' }}>{t('pos_price_suffix')}</span>
                  </p>
                  <p dir="auto" className="text-xs font-bold mb-5" style={{ color: 'var(--text-muted)' }}>{t('pos_plan_everything')}</p>
                  <ul className="space-y-2.5 mb-7 text-sm font-bold" style={{ color: 'var(--text-soft)' }}>
                    {[t('pos_feat_reports'), t('pos_feat_multistore'), t('pos_feat_receipts'), t('pos_feat_printers'), t('pos_plan_support')].map((f) => (
                      <li key={f} dir="auto" className="flex items-start" style={{ gap: 'var(--icon-label-gap-sm)' }}>
                        <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: 'var(--primary)' }} aria-hidden="true" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <a
                    href="#contact"
                    className="sw-btn sw-btn-primary btn-lg w-full mt-auto"
                    onClick={(e) => {
                      trackEvent('cta_click', undefined, { label: 'pricing_pro', location: 'swiftpos_pricing' });
                      scrollToSection(e, 'contact');
                    }}
                  >
                    {t('pos_plan_cta_pro')}
                    <ArrowRight className="w-4 h-4 rtl:rotate-180" aria-hidden="true" />
                  </a>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ============ 8. TESTIMONIALS (placeholder quotes — replace with real customers pre-launch) ============ */}
          <section className="sw-section" role="region" aria-labelledby="swiftpos-testi-title">
            <div className="sw-container">
              <Reveal className="text-center max-w-2xl mx-auto mb-10">
                <span className="sw-eyebrow">{t('pos_testi_eyebrow')}</span>
                <h2 id="swiftpos-testi-title" dir="auto" className="text-3xl md:text-4xl font-black tracking-tight" style={{ color: 'var(--text)' }}>
                  {t('pos_testi_title')}
                </h2>
              </Reveal>
              <motion.div
                className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-5xl mx-auto"
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: '-64px' }}
                variants={staggerParent}
              >
                {TESTIS.map((item) => (
                  <motion.figure key={item.q} variants={staggerChild} className="sw-card p-6 flex flex-col h-full">
                    <Quote className="w-6 h-6 mb-3" style={{ color: 'var(--primary)' }} aria-hidden="true" />
                    <blockquote dir="auto" className="text-[0.95rem] leading-relaxed flex-1" style={{ color: 'var(--text-soft)' }}>
                      “{t(item.q)}”
                    </blockquote>
                    <figcaption dir="auto" className="text-xs font-black uppercase tracking-widest mt-4" style={{ color: 'var(--text-muted)' }}>
                      — {t(item.n)}
                    </figcaption>
                  </motion.figure>
                ))}
              </motion.div>
            </div>
          </section>

          {/* ============ 9. FAQ ============ */}
          <section className="sw-section" role="region" aria-labelledby="swiftpos-faq-title" style={{ paddingTop: 0 }}>
            <div className="sw-container">
              <Reveal className="text-center max-w-2xl mx-auto mb-8">
                <span className="sw-eyebrow">{t('pos_faq_eyebrow')}</span>
                <h2 id="swiftpos-faq-title" dir="auto" className="text-3xl md:text-4xl font-black tracking-tight" style={{ color: 'var(--text)' }}>
                  {t('pos_faq_title')}
                </h2>
              </Reveal>
              <div className="max-w-3xl mx-auto space-y-3">
                {FAQS.map((f, i) => {
                  const open = openFaq === i;
                  return (
                    <div key={f.q} className="sw-card overflow-hidden">
                      <button
                        onClick={() => {
                          setOpenFaq(open ? null : i);
                          if (!open) trackEvent('cta_click', undefined, { label: `faq_${i + 1}`, location: 'swiftpos_faq' });
                        }}
                        aria-expanded={open}
                        aria-controls={`swiftpos-faq-panel-${i}`}
                        id={`swiftpos-faq-button-${i}`}
                        className="w-full flex items-center justify-between text-left p-5 font-bold"
                        style={{ gap: 'var(--icon-label-gap)', color: 'var(--text)', minHeight: 'var(--touch-min)' }}
                      >
                        <span dir="auto">{t(f.q)}</span>
                        <ChevronDown
                          className="w-5 h-5 shrink-0 transition-transform"
                          style={{ color: 'var(--primary)', transform: open ? 'rotate(180deg)' : 'none' }}
                          aria-hidden="true"
                        />
                      </button>
                      {open && (
                        <div id={`swiftpos-faq-panel-${i}`} role="region" aria-labelledby={`swiftpos-faq-button-${i}`}>
                          <p dir="auto" className="px-5 pb-5 text-sm leading-relaxed" style={{ color: 'var(--text-muted)' }}>
                            {t(f.a)}
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          {/* ============ 10. FINAL CTA ============ */}
          <section className="sw-section" role="region" aria-labelledby="swiftpos-final-title" style={{ paddingTop: 0 }}>
            <div className="sw-container">
              <motion.div
                className="max-w-4xl mx-auto"
                initial={{ opacity: 0, y: 36, scale: 0.98 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.7, ease: EASE }}
              >
                <div
                  className="relative overflow-hidden rounded-3xl px-6 py-14 md:p-16 text-center"
                  style={{ background: 'linear-gradient(135deg, var(--primary), #7c3aed)', boxShadow: '0 30px 80px -30px rgba(var(--primary-rgb), 0.6)' }}
                >
                  <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
                    <motion.div
                      className="absolute -top-24 -right-24 w-72 h-72 rounded-full blur-[90px]"
                      style={{ backgroundColor: 'rgba(255, 255, 255, 0.18)' }}
                      animate={{ x: [0, -24, 0], y: [0, 16, 0] }}
                      transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
                    />
                    <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full blur-[90px]" style={{ backgroundColor: 'rgba(0, 0, 0, 0.2)' }} />
                  </div>
                  <h2 id="swiftpos-final-title" dir="auto" className="relative text-3xl md:text-4xl font-black tracking-tight text-white mb-3">
                    {t('pos_final_title')}
                  </h2>
                  <p dir="auto" className="relative max-w-xl mx-auto leading-relaxed mb-8" style={{ color: 'rgba(255, 255, 255, 0.85)' }}>
                    {t('pos_final_desc')}
                  </p>
                  <div className="relative flex flex-col sm:flex-row gap-3 justify-center" role="group" aria-label="Final actions">
                    <motion.a
                      href={APP_LINK}
                      target="_blank"
                      rel="noreferrer"
                      className="sw-btn btn-lg font-black"
                      style={{ backgroundColor: 'white', color: 'var(--primary)' }}
                      whileHover={{ scale: 1.04, y: -2 }}
                      whileTap={{ scale: 0.97 }}
                      onClick={() => trackEvent('swiftpos_get', undefined, { label: 'final_cta', page: 'swiftpos' })}
                    >
                      <Play className="w-4 h-4 fill-current" aria-hidden="true" />
                      {t('pos_hero_play')}
                    </motion.a>
                    <motion.a
                      href="#contact"
                      className="sw-btn btn-lg font-bold text-white"
                      style={{ border: '1px solid rgba(255, 255, 255, 0.4)', backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
                      whileHover={{ scale: 1.04, y: -2 }}
                      whileTap={{ scale: 0.97 }}
                      onClick={(e) => {
                        trackEvent('cta_click', undefined, { label: 'contact_swiftech', location: 'swiftpos_final_cta' });
                        scrollToSection(e, 'contact');
                      }}
                    >
                      {t('pos_final_contact')}
                    </motion.a>
                  </div>
                  <p dir="auto" className="relative text-xs font-bold mt-5" style={{ color: 'rgba(255, 255, 255, 0.75)' }}>
                    {t('pos_hero_free_note')}
                  </p>
                </div>
              </motion.div>
            </div>
          </section>
        </main>

        {/* ============ STICKY MOBILE INSTALL BAR ============ */}
        {showSticky && !stickyClosed && (
          <div
            className="sm:hidden fixed bottom-0 inset-x-0"
            style={{
              zIndex: 'var(--z-sticky)',
              backgroundColor: 'var(--surface-glass)',
              backdropFilter: 'blur(16px)',
              WebkitBackdropFilter: 'blur(16px)',
              borderTop: '1px solid var(--border)',
              paddingBottom: 'env(safe-area-inset-bottom)',
            }}
            role="region"
            aria-label="Get SwiftPOS"
          >
            <div className="flex items-center px-4 py-3" style={{ gap: 'var(--icon-label-gap)' }}>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-black truncate" style={{ color: 'var(--text)' }}>SwiftPOS</p>
                <p className="text-[11px] font-bold" style={{ color: 'var(--text-muted)' }}>{t('pos_hero_free_note')}</p>
              </div>
              <a
                href={APP_LINK}
                target="_blank"
                rel="noreferrer"
                className="sw-btn sw-btn-primary"
                style={{ padding: '10px 20px', fontSize: '0.85rem' }}
                onClick={() => trackEvent('swiftpos_get', undefined, { label: 'sticky_bar', page: 'swiftpos' })}
              >
                <Play className="w-4 h-4 fill-current" aria-hidden="true" />
                {t('pos_plan_cta_free')}
              </a>
              <button
                onClick={() => setStickyClosed(true)}
                aria-label="Dismiss"
                className="p-2 -me-1 rounded-lg"
                style={{ color: 'var(--text-muted)', minWidth: 'var(--touch-min)', minHeight: 'var(--touch-min)' }}
              >
                <X className="w-4 h-4" aria-hidden="true" />
              </button>
            </div>
          </div>
        )}

        <Footer />
      </div>
    </MotionConfig>
  );
};

export default SwiftPOS;
